import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, MinusCircle } from "lucide-react";

const AUDIT_CHANNELS = [
  { id: "bri_link", label: "BRI Link", formula: "awal + pengeluaran − pemasukan" },
  { id: "bsi", label: "BSI", formula: "awal + pengeluaran − pemasukan" },
  { id: "order", label: "Order Kuota", formula: "awal − pemasukan" },
  { id: "seabank", label: "Seabank", formula: "awal − pemasukan" },
];
const num = (v) => parseInt(v || "0", 10) || 0;
const fmtSelisih = (s) => (s > 0 ? "+" : s < 0 ? "−" : "") + rupiah(Math.abs(s));
const fmtNum = (v) => (v < 0 ? "−" : "") + rupiah(Math.abs(v));

const StatusBadge = ({ aktual, selisih, keterangan, idPrefix }) => {
  if (aktual === null || aktual === undefined || aktual === "")
    return <span data-testid={`${idPrefix}-status`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-stone-100 text-stone-500"><MinusCircle className="w-3 h-3" /> Belum diisi kasir</span>;
  if (selisih === 0)
    return <span data-testid={`${idPrefix}-status`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><ShieldCheck className="w-3 h-3" /> Sesuai</span>;
  if ((keterangan || "").trim())
    return <span data-testid={`${idPrefix}-status`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"><AlertTriangle className="w-3 h-3" /> Selisih {fmtSelisih(selisih)} · ada keterangan</span>;
  return <span data-testid={`${idPrefix}-status`} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700"><AlertTriangle className="w-3 h-3" /> Selisih {fmtSelisih(selisih)} · TANPA keterangan</span>;
};

export default function AuditTab() {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [date, setDate] = useState(() => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const month = date.slice(0, 7);

  useEffect(() => {
    api.get("/branches").then((r) => { setBranches(r.data); setBranchId((prev) => prev || (r.data.length ? r.data[0].id : "")); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!branchId || !date) return;
    setLoading(true);
    api.get(`/finance/report?branch_id=${branchId}&date=${date}`)
      .then((r) => setData(r.data))
      .catch(() => { setData(null); toast.error("Gagal memuat data audit"); })
      .finally(() => setLoading(false));
  }, [branchId, date]);

  useEffect(() => {
    if (!branchId) return;
    api.get(`/finance/reports?branch_id=${branchId}&month=${month}`).then((r) => setHistory(r.data)).catch(() => {});
  }, [branchId, month]);

  const rep = data?.report;
  const ctx = data?.audit_ctx;
  const entries = rep?.entries || [];
  const audit = rep?.audit || {};

  const chTotals = (cid) => ({
    masuk: entries.filter((e) => e.channel === cid && e.arah === "in").reduce((s, e) => s + num(e.amount), 0),
    keluar: entries.filter((e) => e.channel === cid && e.arah === "out").reduce((s, e) => s + num(e.amount), 0),
  });
  const expected = (cid) => {
    const { masuk, keluar } = chTotals(cid);
    const sa = audit[cid]?.saldo_awal ?? ctx?.saldo_awal_defaults?.[cid] ?? 0;
    return cid === "bri_link" || cid === "bsi" ? sa + keluar - masuk : sa - masuk;
  };
  const aktualOf = (cid) => audit[cid]?.aktual ?? null;
  const selisihOf = (cid) => (aktualOf(cid) === null ? null : num(aktualOf(cid)) - expected(cid));
  const saldoAwalOf = (cid) => audit[cid]?.saldo_awal ?? ctx?.saldo_awal_defaults?.[cid] ?? 0;

  const brankasHasPrev = !!ctx?.prev_date;
  const brankasExp = num(ctx?.brankas?.prev_simpan) + num(ctx?.brankas?.prev_saldo_akhir) - num(rep?.saldo_awal);
  const brankasAktual = audit.brankas?.aktual ?? null;
  const brankasSelisih = !brankasHasPrev || brankasAktual === null ? null : num(brankasAktual) - brankasExp;

  const issues = rep ? AUDIT_CHANNELS.filter((c) => { const s = selisihOf(c.id); return s !== null && s !== 0 && !(audit[c.id]?.keterangan || "").trim(); }).length + (brankasSelisih !== null && brankasSelisih !== 0 && !(audit.brankas?.keterangan || "").trim() ? 1 : 0) : 0;
  const anyFilled = rep && (AUDIT_CHANNELS.some((c) => aktualOf(c.id) !== null) || brankasAktual !== null);

  return (
    <div className="space-y-4" data-testid="audit-tab">
      <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
        <div className="flex flex-wrap gap-2 items-center">
          <select data-testid="audit-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white">
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <input data-testid="audit-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white" />
          {rep && !anyFilled && <span data-testid="audit-pending-badge" className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-stone-100 text-stone-500">Audit belum diisi kasir</span>}
          {rep && anyFilled && issues > 0 && <span data-testid="audit-issue-count" className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-rose-100 text-rose-700">{issues} selisih tanpa keterangan</span>}
          {rep && anyFilled && issues === 0 && <span data-testid="audit-ok-badge" className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">Tidak ada masalah audit</span>}
        </div>
        {history.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            <span className="text-[10px] font-mono uppercase tracking-widest text-stone-400 self-center">Tersimpan:</span>
            {history.map((h) => (
              <button key={h.id} data-testid={`audit-history-${h.date}`} onClick={() => setDate(h.date)} className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${h.date === date ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-stone-600 border-[#E5DECF]"}`}>{h.date.slice(8)}/{h.date.slice(5, 7)}</button>
            ))}
          </div>
        )}
      </div>

      {loading && <div className="bg-white rounded-2xl border border-[#E5DECF] p-6 text-center text-sm text-stone-400">Memuat…</div>}

      {!loading && !rep && (
        <div className="bg-white rounded-2xl border border-[#E5DECF] p-6 text-center text-sm text-stone-400" data-testid="audit-empty">
          Belum ada laporan keuangan untuk tanggal ini.
        </div>
      )}

      {!loading && rep && (
        <>
          <div className="bg-white rounded-2xl border border-[#E5DECF] p-4">
            <h3 className="font-display font-black text-base mb-1">Laporan Audit Channel</h3>
            <p className="text-[11px] text-stone-400 mb-3">
              Cabang: <b>{branches.find((b) => b.id === branchId)?.name}</b> · Tanggal {date.split("-").reverse().join("/")}
              {ctx?.prev_date && <> · saldo awal dari audit {ctx.prev_date.split("-").reverse().join("/")}</>}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[720px]">
                <thead>
                  <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]">
                    <th className="py-2 pr-2">Channel</th><th className="py-2 pr-2">Saldo Awal</th><th className="py-2 pr-2">Pemasukan</th><th className="py-2 pr-2">Pengeluaran</th>
                    <th className="py-2 pr-2">Saldo Akhir (Seharusnya)</th><th className="py-2 pr-2">Aktual Kasir</th><th className="py-2 pr-2">Selisih</th><th className="py-2 pr-2">Status</th><th className="py-2">Keterangan</th>
                  </tr>
                </thead>
                <tbody>
                  {AUDIT_CHANNELS.map((c) => {
                    const { masuk, keluar } = chTotals(c.id);
                    const aktual = aktualOf(c.id);
                    const sel = selisihOf(c.id);
                    return (
                      <tr key={c.id} className="border-b border-stone-100 last:border-0" data-testid={`audit-row-${c.id}`}>
                        <td className="py-2 pr-2 font-semibold">{c.label}<div className="text-[9px] font-mono text-stone-400 font-normal">{c.formula}</div></td>
                        <td className="py-2 pr-2">{rupiah(saldoAwalOf(c.id))}</td>
                        <td className="py-2 pr-2 text-emerald-700">{rupiah(masuk)}</td>
                        <td className="py-2 pr-2 text-rose-600">{rupiah(keluar)}</td>
                        <td className="py-2 pr-2 font-bold">{fmtNum(expected(c.id))}</td>
                        <td className="py-2 pr-2 font-bold">{aktual === null ? "—" : rupiah(num(aktual))}</td>
                        <td className={`py-2 pr-2 font-black ${sel === null ? "text-stone-400" : sel === 0 ? "text-emerald-700" : "text-rose-600"}`}>{sel === null ? "—" : fmtSelisih(sel)}</td>
                        <td className="py-2 pr-2"><StatusBadge aktual={aktual} selisih={sel} keterangan={audit[c.id]?.keterangan} idPrefix={`audit-row-${c.id}`} /></td>
                        <td className="py-2 text-stone-600">{audit[c.id]?.keterangan || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-amber-200 p-4" data-testid="audit-brankas-card">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-display font-black text-base">🏦 Saldo Simpan Brankas</h3>
              <StatusBadge aktual={brankasAktual} selisih={brankasSelisih} keterangan={audit.brankas?.keterangan} idPrefix="audit-brankas" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
              <div className="rounded-xl bg-stone-50 p-3"><div className="text-stone-500">Uang Disimpan Kemarin</div><div className="font-bold mt-1">{rupiah(num(ctx?.brankas?.prev_simpan))}</div></div>
              <div className="rounded-xl bg-stone-50 p-3"><div className="text-stone-500">Saldo Akhir Kemarin</div><div className="font-bold mt-1">{rupiah(num(ctx?.brankas?.prev_saldo_akhir))}</div></div>
              <div className="rounded-xl bg-stone-50 p-3"><div className="text-stone-500">Saldo Awal Hari Ini</div><div className="font-bold mt-1">{rupiah(num(rep.saldo_awal))}</div></div>
              <div className="rounded-xl bg-emerald-700 p-3 text-white"><div className="text-emerald-200">Brankas Seharusnya</div><div data-testid="audit-brankas-expected-val" className="font-black mt-1">{brankasHasPrev ? fmtNum(brankasExp) : "—"}</div>{!brankasHasPrev && <div className="text-[9px] text-emerald-200">belum ada laporan hari sebelumnya</div>}</div>
              <div className="rounded-xl bg-stone-50 p-3"><div className="text-stone-500">Brankas Aktual</div><div className="font-bold mt-1">{brankasAktual === null ? "—" : rupiah(num(brankasAktual))}</div></div>
              <div className="rounded-xl bg-stone-50 p-3"><div className="text-stone-500">Selisih</div><div className={`font-black mt-1 ${brankasSelisih === null ? "text-stone-400" : brankasSelisih === 0 ? "text-emerald-700" : "text-rose-600"}`}>{brankasSelisih === null ? "—" : fmtSelisih(brankasSelisih)}</div></div>
            </div>
            {audit.brankas?.keterangan && <p className="text-xs text-stone-600 mt-2">Keterangan: {audit.brankas.keterangan}</p>}
          </div>
        </>
      )}
    </div>
  );
}
